import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Plus, Trash2, Download, Upload, RotateCcw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { validateProfile, createDefaultProfile } from '../../../lib/uds/profile';
import { dtcCodeToBytes } from '../../../lib/uds/dtc';
import { hex2 } from '../../../lib/uds/constants';

// Shows a small label above each field only on narrow screens, where the row
// collapses from a compact grid into stacked fields and column headers no
// longer apply.
const FieldGroup = ({ label, className, children }) => (
  <div className={className}>
    <span className="block sm:hidden text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</span>
    {children}
  </div>
);

function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const AuthorProfileEditor = ({ profile, onApply, onExportLog }) => {
  const [draft, setDraft] = useState(profile);
  const [errors, setErrors] = useState([]);
  const [applied, setApplied] = useState(false);
  const fileInputRef = useRef(null);

  const updateDid = (i, patch) => setDraft((d) => ({ ...d, dids: d.dids.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const removeDid = (i) => setDraft((d) => ({ ...d, dids: d.dids.filter((_, j) => j !== i) }));
  const addDid = () => setDraft((d) => ({ ...d, dids: [...d.dids, { id: 0xf1a0 + d.dids.length, name: 'New DID', format: 'ascii', length: 4, writable: true, defaultValue: 'TEST' }] }));

  const updateDtc = (i, patch) => setDraft((d) => ({ ...d, dtcs: d.dtcs.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const removeDtc = (i) => setDraft((d) => ({ ...d, dtcs: d.dtcs.filter((_, j) => j !== i) }));
  const addDtc = () => setDraft((d) => ({ ...d, dtcs: [...d.dtcs, { bytes: dtcCodeToBytes('P0000'), code: 'P0000', description: 'New Fault', initialStatus: 0x00, faultActive: false, snapshot: { did: 0xf186, label: 'Session at Fault', value: null } }] }));

  const setDtcCode = (i, code) => {
    try {
      const bytes = dtcCodeToBytes(code);
      updateDtc(i, { code: code.toUpperCase(), bytes });
    } catch {
      updateDtc(i, { code });
    }
  };

  const handleApply = () => {
    const errs = validateProfile(draft);
    setErrors(errs);
    if (errs.length === 0) {
      onApply(draft);
      setApplied(true);
      setTimeout(() => setApplied(false), 2000);
    }
  };

  const handleExportProfile = () => downloadJson(draft, `uds-profile-${draft.name.toLowerCase().replace(/\s+/g, '-')}.json`);

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const errs = validateProfile(parsed);
        if (errs.length) {
          setErrors(errs);
          return;
        }
        setDraft(parsed);
        setErrors([]);
        onApply(parsed);
      } catch {
        setErrors(['Selected file is not valid JSON.']);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <Card className="technical-card" data-testid="uds-author-editor">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Author Mode — Custom ECU Profile</CardTitle>
        <CardDescription>Define custom DIDs, DTCs, and the Security Access algorithm, then apply the profile to the running simulator.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Profile Name</Label>
            <Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} data-testid="uds-profile-name" />
          </div>
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <Input value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Data Identifiers (DIDs)</Label>
            <Button variant="outline" size="sm" onClick={addDid}><Plus className="h-3.5 w-3.5" /> Add DID</Button>
          </div>
          <div className="space-y-2">
            {draft.dids.map((d, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:items-center bg-muted/30 rounded-md p-2">
                <FieldGroup label="DID (hex)" className="col-span-2 sm:col-span-2">
                  <Input className="font-mono text-xs" value={hex2(d.id >> 8) + hex2(d.id & 0xff)} onChange={(e) => updateDid(i, { id: parseInt(e.target.value, 16) || 0 })} disabled={!!d.computed} />
                </FieldGroup>
                <FieldGroup label="Name" className="col-span-2 sm:col-span-3">
                  <Input className="text-xs" value={d.name} onChange={(e) => updateDid(i, { name: e.target.value })} />
                </FieldGroup>
                <FieldGroup label="Format" className="col-span-1 sm:col-span-2">
                  <Select value={d.format} onValueChange={(v) => updateDid(i, { format: v })} disabled={!!d.computed}>
                    <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ascii">ascii</SelectItem>
                      <SelectItem value="hex">hex</SelectItem>
                      <SelectItem value="numeric">numeric</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldGroup>
                <FieldGroup label="Length" className="col-span-1 sm:col-span-1">
                  <Input className="text-xs" type="number" min={1} value={d.length} onChange={(e) => updateDid(i, { length: Number(e.target.value) || 1 })} disabled={!!d.computed} />
                </FieldGroup>
                <FieldGroup label="Default Value" className="col-span-2 sm:col-span-3">
                  <Input className="text-xs font-mono" value={d.defaultValue ?? ''} onChange={(e) => updateDid(i, { defaultValue: e.target.value })} disabled={!!d.computed} placeholder={d.computed ? 'computed' : 'default value'} />
                </FieldGroup>
                <div className="col-span-1 sm:col-span-1 flex sm:justify-center items-center gap-2">
                  <label className="flex items-center gap-1 text-[10px]">
                    <input type="checkbox" checked={!!d.writable} onChange={(e) => updateDid(i, { writable: e.target.checked })} disabled={!!d.computed} />
                    <span className="sm:hidden">Writable</span>
                  </label>
                </div>
                <div className="col-span-1 sm:col-span-1 flex sm:justify-center items-center">
                  <Button variant="ghost" size="icon" onClick={() => removeDid(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground hidden sm:block">Columns: DID hex, name, format, byte length, default value, writable, remove. The Active Diagnostic Session DID is computed and can't be edited.</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Diagnostic Trouble Codes (DTCs)</Label>
            <Button variant="outline" size="sm" onClick={addDtc}><Plus className="h-3.5 w-3.5" /> Add DTC</Button>
          </div>
          <div className="space-y-2">
            {draft.dtcs.map((d, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:items-center bg-muted/30 rounded-md p-2">
                <FieldGroup label="Code" className="col-span-1 sm:col-span-2">
                  <Input className="font-mono text-xs" value={d.code} onChange={(e) => setDtcCode(i, e.target.value)} />
                </FieldGroup>
                <FieldGroup label="Description" className="col-span-2 sm:col-span-4">
                  <Input className="text-xs" value={d.description} onChange={(e) => updateDtc(i, { description: e.target.value })} />
                </FieldGroup>
                <FieldGroup label="Initial Status (hex)" className="col-span-1 sm:col-span-2">
                  <Input className="font-mono text-xs" value={hex2(d.initialStatus)} onChange={(e) => updateDtc(i, { initialStatus: parseInt(e.target.value, 16) || 0 })} title="Initial status byte (hex)" />
                </FieldGroup>
                <label className="col-span-2 sm:col-span-3 flex items-center gap-1 text-[10px]">
                  <input type="checkbox" checked={!!d.faultActive} onChange={(e) => updateDtc(i, { faultActive: e.target.checked })} />
                  Fault condition still active
                </label>
                <div className="col-span-2 sm:col-span-1 flex sm:justify-center items-center">
                  <Button variant="ghost" size="icon" onClick={() => removeDtc(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">"Fault condition still active" lets this DTC reappear after being cleared, when fault injection is enabled in Practitioner mode.</p>
        </div>

        <div className="space-y-2">
          <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Security Access Algorithm</Label>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm">KEY = SEED</span>
            <Select value={draft.security.type} onValueChange={(v) => setDraft((d) => ({ ...d, security: { ...d.security, type: v } }))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="xor">XOR</SelectItem>
                <SelectItem value="add">+</SelectItem>
              </SelectContent>
            </Select>
            <Input
              className="w-32 font-mono"
              value={`0x${draft.security.value.toString(16).toUpperCase().padStart(4, '0')}`}
              onChange={(e) => setDraft((d) => ({ ...d, security: { ...d.security, value: parseInt(e.target.value.replace(/0x/i, ''), 16) || 0 } }))}
            />
          </div>
        </div>

        {errors.length > 0 && (
          <div className="text-sm text-destructive space-y-1" data-testid="uds-profile-errors">
            {errors.map((e, i) => <div key={i} className="flex items-center gap-2"><AlertCircle className="h-3.5 w-3.5 shrink-0" />{e}</div>)}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-structure">
          <Button className="btn-premium" onClick={handleApply} data-testid="uds-apply-profile-btn">
            {applied ? <CheckCircle2 className="h-4 w-4" /> : null} Apply Profile to Simulator
          </Button>
          <Button variant="outline" onClick={handleExportProfile} data-testid="uds-export-profile-btn"><Download className="h-4 w-4" /> Export Profile JSON</Button>
          <Button variant="outline" onClick={handleImportClick} data-testid="uds-import-profile-btn"><Upload className="h-4 w-4" /> Import Profile JSON</Button>
          <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={handleImportFile} />
          <Button variant="outline" onClick={() => setDraft(createDefaultProfile())}><RotateCcw className="h-4 w-4" /> Reset to Default</Button>
          <Button variant="outline" onClick={onExportLog} data-testid="uds-export-log-btn-author"><Download className="h-4 w-4" /> Export Session Log</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AuthorProfileEditor;
